import type { ReactNode } from 'react';
import {
  useActivityQuery, useConnectionsQuery, useDestinationsQuery, useOverviewQuery, useReviewCountQuery, useUsageQuery,
  type ActivityEvent, type Me,
} from './api.js';
import { Card, Spinner } from './ui.js';
import {
  connectionsReady, greeting, keapReady, relativeTime, setupComplete, setupSteps,
  sourceStates, type SetupStep, type StepId,
} from './setupModel.js';
import {
  AlertIcon, CameraGlyph, CheckCircleIcon, ChevronRight, KeyIcon, MailIcon, PlaneIcon, ReviewIcon,
  SheetIcon, TrayIcon,
} from './icons.js';

/**
 * Where the workspace stands, and the next useful thing to do.
 *
 * Every number is a real workspace-scoped count from an existing endpoint, defined in
 * docs/contactpilot/dashboard-metrics.md. Nothing is a sample, a rate, or a trend arrow:
 * a figure nobody can trace back to a row is worse than no figure. While a number is
 * loading it shows a dash, never a zero that would read as "nothing happened".
 */
export function Dashboard({ me, workspaceName, onNavigate }: {
  me: Me;
  workspaceName: string | null;
  onNavigate: (to: 'capture' | 'review' | 'review-sent' | 'connections' | 'usage' | 'email-capture' | 'integrations') => void;
}) {
  const overview = useOverviewQuery(undefined, { pollingInterval: 60_000 });
  const review = useReviewCountQuery(undefined, { pollingInterval: 30_000 });
  const conn = useConnectionsQuery();
  const usage = useUsageQuery();
  const activity = useActivityQuery({ scope: 'customer' }, { pollingInterval: 60_000 });
  const dest = useDestinationsQuery(undefined, { pollingInterval: 60_000 });
  const activeDestinations = dest.data?.destinations?.filter((d) => d.status === 'active') ?? [];
  const failedDeliveries = dest.data?.deliveries?.filter((d) => d.status === 'failed').length ?? 0;
  const pendingDeliveries = dest.data?.deliveries?.filter((d) => d.status === 'pending').length ?? 0;

  const connectors = conn.data?.connectors;
  const ov = overview.data;
  const steps = setupSteps({
    connectors, overview: ov, attention: review.data?.attention, reviewTotal: review.data?.total,
    activeDestinations: dest.data?.destinations ? activeDestinations.length : undefined,
  });
  const loaded = !!ov && !!conn.data && !!review.data;
  const showSetup = loaded && !setupComplete(steps);
  const canAdmin = me.role === 'owner' || me.role === 'admin';
  const ready = connectionsReady(connectors);
  const sources = sourceStates(connectors);
  const crmLimit = usage.data?.dimensions.find((d) => d.key === 'crm_changes_daily');
  const dash = (n: number | undefined) => (n === undefined ? '—' : n.toLocaleString());

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{greeting()}</h1>
          <p className="mt-1 text-sm text-slate-600">
            Here’s what’s happening in <span className="font-medium text-slate-800">{workspaceName ?? 'your workspace'}</span>.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
        </p>
      </header>

      {/* ── Four numbers, each a link to where you act on it ──────────── */}
      <section aria-label="Summary">
        <ul className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
          <StatTile
            icon={<ReviewIcon />} tint="bg-cp-sky text-cp-blue-600"
            value={dash(review.data?.attention)} label="Ready for review"
            hint="Waiting for your approval or a fix"
            onClick={() => onNavigate('review')} action="Open Review"
          />
          <StatTile
            icon={<PlaneIcon />} tint="bg-cp-mint text-cp-teal-ink"
            value={dash(ov?.crm?.contactsCreatedLast7Days)} label="Added to Keap"
            hint="New contacts Keap confirmed, last 7 days"
            onClick={() => onNavigate('review-sent')} action="See what was sent"
          />
          <StatTile
            icon={<KeyIcon />} tint="bg-violet-50 text-violet-700"
            value={conn.data ? `${ready.ready} of ${ready.of}` : '—'} label="Connections ready"
            hint="Keap, Anthropic key, and a mailbox"
            onClick={() => onNavigate('connections')} action="Manage connections"
          />
          <StatTile
            icon={<CheckCircleIcon />} tint="bg-emerald-50 text-emerald-700"
            value={dash(ov?.crm?.succeeded)} label="CRM changes confirmed"
            hint={ov?.crm
              ? `${ov.crm.failed.toLocaleString()} failed · ${ov.crm.pending.toLocaleString()} in progress`
              : 'Every change Keap confirmed'}
            onClick={() => onNavigate('review-sent')} action="See CRM results"
          />
        </ul>
      </section>

      {showSetup ? <GetStarted steps={steps} canAdmin={canAdmin} onNavigate={onNavigate} /> : null}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── Capture shortcuts ─────────────────────────────────────────── */}
        <Card className="p-5 lg:col-span-2">
          <h2 className="text-lg font-semibold text-slate-900">Capture new contacts</h2>
          <p className="mt-1 text-sm text-slate-600">Bring in the people you meet and the conversations you have.</p>
          <ul className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Shortcut icon={<CameraGlyph />} tint="bg-cp-sky text-cp-blue-600" title="Scan business card"
              status={sources.card.status} onClick={() => onNavigate('capture')} />
            <Shortcut icon={<MailIcon />} tint="bg-rose-50 text-rose-700" title="From Gmail"
              status={sources.gmail.status} onClick={() => onNavigate(sources.gmail.status === 'Connected' ? 'email-capture' : 'connections')} />
            <Shortcut icon={<TrayIcon />} tint="bg-cp-blue-50 text-cp-blue-700" title="From Outlook"
              status={sources.outlook.status} onClick={() => onNavigate(sources.outlook.status === 'Connected' ? 'email-capture' : 'connections')} />
            <Shortcut icon={<SheetIcon />} tint="bg-cp-mint text-cp-teal-ink" title="Import CSV"
              status={sources.csv.status} onClick={() => onNavigate('capture')} />
          </ul>
        </Card>

        {/* ── Recent activity ───────────────────────────────────────────── */}
        <Card className="p-5">
          <h2 className="text-lg font-semibold text-slate-900">Recent activity</h2>
          <RecentActivity loading={activity.isLoading} events={activity.data?.events ?? []} />
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── CRM status ────────────────────────────────────────────────── */}
        <Card className="p-5">
          <h2 className="text-lg font-semibold text-slate-900">Your CRM</h2>
          {conn.data ? (
            keapReady(connectors) ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-emerald-800">
                <CheckCircleIcon size={18} /> Keap is connected and verified.
              </p>
            ) : (
              <p className="mt-2 flex items-center gap-2 text-sm text-amber-900">
                <AlertIcon size={18} /> Keap is not connected. Approved contacts cannot be sent yet.
              </p>
            )
          ) : <div className="mt-2"><Spinner /></div>}
          <p className="mt-2 text-xs text-slate-500">
            Nothing is sent to Keap until you approve it, and a contact shows as sent only after Keap confirms it.
          </p>
          {dest.data?.destinations ? (
            <p className="mt-2 text-sm text-slate-700" data-destinations-summary="">
              {activeDestinations.length === 0
                ? 'No Zapier or webhook destination is on.'
                : `Also sent to ${activeDestinations.map((d) => d.name).join(', ')}.`}
              {failedDeliveries > 0 ? <span className="block font-medium text-red-700">{failedDeliveries} recent deliver{failedDeliveries === 1 ? 'y' : 'ies'} failed.</span> : null}
              {pendingDeliveries > 0 ? <span className="block text-slate-600">{pendingDeliveries} sending.</span> : null}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-x-4">
            <LinkButton onClick={() => onNavigate('connections')}>CRM connection</LinkButton>
            <LinkButton onClick={() => onNavigate('integrations')}>Integrations</LinkButton>
          </div>
        </Card>

        {/* ── Usage ─────────────────────────────────────────────────────── */}
        <Card className="p-5 lg:col-span-2">
          <h2 className="text-lg font-semibold text-slate-900">Today’s CRM changes</h2>
          {crmLimit && crmLimit.limit === 0 ? (
            // No CRM sending authorised here: a 0-of-0 meter would say nothing true.
            <p className="mt-2 text-sm text-slate-600">CRM sending is not switched on for this workspace yet, so no changes can be sent today.</p>
          ) : crmLimit && crmLimit.limit !== null ? (
            <UsageBar
              used={(crmLimit.used ?? 0) + crmLimit.reserved}
              limit={crmLimit.limit}
              label={crmLimit.control === 'safety' ? 'Daily safety limit' : 'Daily allowance'}
            />
          ) : usage.isError || (crmLimit && crmLimit.limit === null) ? (
            <p className="mt-2 text-sm text-slate-600">No daily limit is shown for this workspace.</p>
          ) : <div className="mt-2"><Spinner /></div>}
          {crmLimit ? <LinkButton onClick={() => onNavigate('usage')}>Usage &amp; limits</LinkButton> : null}
        </Card>
      </div>
    </div>
  );
}

function StatTile({ icon, tint, value, label, hint, onClick, action }: {
  icon: ReactNode; tint: string; value: string; label: string; hint: string; onClick: () => void; action: string;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-label={`${label}: ${value}. ${action}`}
        className="flex h-full w-full flex-col rounded-card border border-cp-border bg-white p-4 text-left shadow-card transition hover:-translate-y-0.5 hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600 motion-reduce:transform-none sm:p-5"
      >
        <span className={`grid h-10 w-10 place-items-center rounded-xl ${tint}`}>{icon}</span>
        <span className="mt-3 text-2xl font-bold tabular-nums text-slate-900 sm:text-3xl">{value}</span>
        <span className="mt-0.5 text-sm font-medium text-slate-800">{label}</span>
        <span className="mt-0.5 text-xs text-slate-500">{hint}</span>
      </button>
    </li>
  );
}

function Shortcut({ icon, tint, title, status, onClick }: {
  icon: ReactNode; tint: string; title: string; status: string; onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex h-full min-h-11 w-full flex-col items-center rounded-2xl border border-cp-border bg-slate-50 px-3 py-4 text-center transition hover:border-cp-blue-500 hover:bg-cp-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600"
      >
        <span className={`grid h-11 w-11 place-items-center rounded-2xl ${tint}`}>{icon}</span>
        <span className="mt-2 text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 text-xs text-slate-500">{status}</span>
      </button>
    </li>
  );
}

const KIND_TINT = {
  card: 'bg-cp-sky text-cp-blue-600',
  email: 'bg-rose-50 text-rose-700',
  csv: 'bg-cp-mint text-cp-teal-ink',
  crm: 'bg-emerald-50 text-emerald-700',
  problem: 'bg-amber-50 text-amber-800',
} as const;
const KIND_ICON = {
  card: <CameraGlyph size={18} />, email: <MailIcon size={18} />, csv: <SheetIcon size={18} />,
  crm: <PlaneIcon size={18} />, problem: <AlertIcon size={18} />,
} as const;

function RecentActivity({ loading, events }: {
  loading: boolean; events: readonly ActivityEvent[];
}) {
  if (loading) return <div className="mt-3"><Spinner /></div>;
  const shown = events.filter((e) => e.title && e.kind).slice(0, 6);
  if (shown.length === 0) {
    return (
      <p className="mt-3 text-sm text-slate-600">
        Nothing yet. Captures, imports and CRM results will appear here.
      </p>
    );
  }
  return (
    <ul className="mt-3 divide-y divide-slate-100">
      {shown.map((e) => {
        const t = { title: e.title!, kind: e.kind! };
        return (
          <li key={e.id} className="flex items-center gap-3 py-2.5">
            <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${KIND_TINT[t.kind]}`}>{KIND_ICON[t.kind]}</span>
            <span className="min-w-0 flex-1 text-sm text-slate-800">{t.title}</span>
            <time dateTime={e.occurredAt} className="shrink-0 text-xs text-slate-500">{relativeTime(e.occurredAt)}</time>
          </li>
        );
      })}
    </ul>
  );
}

function UsageBar({ used, limit, label }: { used: number; limit: number; label: string }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="mt-3">
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-semibold tabular-nums text-slate-900">{used.toLocaleString()} of {limit.toLocaleString()} used</span>
      </div>
      <div
        className="mt-2 h-2.5 overflow-hidden rounded-full bg-slate-100"
        role="progressbar" aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used} aria-label={label}
      >
        <div className="h-full rounded-full bg-gradient-to-r from-cp-blue-500 to-cp-teal" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function LinkButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button" onClick={onClick}
      className="mt-2 inline-flex min-h-11 items-center gap-1 text-sm font-semibold text-cp-blue-600 hover:text-cp-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600"
    >
      {children}<ChevronRight size={16} />
    </button>
  );
}

const STEP_TARGET: Partial<Record<StepId, { to: 'capture' | 'review' | 'review-sent' | 'connections' | 'integrations'; label: string; adminOnly?: boolean }>> = {
  crm: { to: 'connections', label: 'Connect Keap', adminOnly: true },
  destinations: { to: 'integrations', label: 'Integrations', adminOnly: true },
  ai: { to: 'connections', label: 'Add key', adminOnly: true },
  capture: { to: 'capture', label: 'Capture' },
  review: { to: 'review', label: 'Open Review' },
  result: { to: 'review-sent', label: 'See results' },
};

/**
 * The guided start. Shown while a required step is open; every tick is read from the
 * server, so leaving and coming back resumes exactly where the work is.
 */
function GetStarted({ steps, canAdmin, onNavigate }: {
  steps: readonly SetupStep[]; canAdmin: boolean;
  onNavigate: (to: 'capture' | 'review' | 'review-sent' | 'connections' | 'integrations') => void;
}) {
  const done = steps.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done && !s.optional) ?? steps.find((s) => !s.done);
  return (
    <section aria-labelledby="get-started" className="rounded-card border border-cp-border bg-gradient-to-br from-white via-white to-cp-sky/60 p-5 shadow-card">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="get-started" className="text-lg font-semibold text-slate-900">Get started with ContactPilot</h2>
        <p className="text-sm text-slate-600">{done} of {steps.length} done</p>
      </div>
      <ol className="mt-4 space-y-2">
        {steps.map((s, i) => {
          const target = STEP_TARGET[s.id];
          const isNext = next?.id === s.id;
          return (
            <li
              key={s.id}
              className={`flex flex-wrap items-start gap-3 rounded-2xl border p-3 sm:flex-nowrap ${isNext ? 'border-cp-blue-500 bg-white' : 'border-transparent'}`}
              aria-current={isNext ? 'step' : undefined}
            >
              <span
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-sm font-semibold ${s.done ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                aria-hidden="true"
              >
                {s.done ? '✓' : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">
                  {s.title}
                  {s.optional ? <span className="ml-2 text-xs font-normal text-slate-500">Optional</span> : null}
                  <span className="sr-only">{s.done ? ' — done' : ' — not done yet'}</span>
                </p>
                <p className="text-sm text-slate-600">{s.detail}</p>
                {!s.done && target?.adminOnly && !canAdmin
                  ? <p className="mt-1 text-xs text-slate-500">Ask a workspace owner or admin to do this.</p>
                  : null}
              </div>
              {!s.done && target && (!target.adminOnly || canAdmin) ? (
                <button
                  type="button"
                  onClick={() => onNavigate(target.to)}
                  className={`inline-flex min-h-11 shrink-0 items-center rounded-xl px-4 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-blue-600 ${isNext ? 'bg-cp-blue-600 text-white hover:bg-cp-blue-700' : 'border border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`}
                >
                  {target.label}
                </button>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
